import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { Map, List, fromJS } from 'immutable'
import { Modifier, EditorState, SelectionState } from 'draft-js'

// suggestions plugin from rdf-ui
import createSuggestionsPlugin from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/suggestionsPlugin'
import ErrorsDecoratorFactory from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/decorators/ErrorsDecoratorFactory'

// suggestions plugin from stats
import extractAytmLogic from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/lib/extract-aytm-logic'
import extractSelection from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/lib/extract-selection'
import extractParserResult from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/lib/extract-parser-result'
import suggestionsBuilders from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/builders'
import SuggestionsComponent from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/components/Suggestions'

import ErrorsDecoratorStrategy from '@aytm/app/stats/lib/render/draft-js/decorators/errors-decorator-strategy'
import WarningsDecoratorStrategy from '@aytm/app/stats/lib/render/draft-js/decorators/warnings-decorator-strategy'

// components
import RichText from '@aytm/lib/rfd-ui/components/advanced/rich-text'

// styles
import '@aytm/app/stats/page/styles/globals.scss'

import style from '@aytm/app/stats/components/advanced-filter/styles/AdvancedFilter.module.scss'

const AYMT_LOCALE = {
  referenceWarning: 'REFERENCE WARNING',
  referenceError: 'REFERENCE ERROR',
  questionDoesntExist: "Referred field {index} doesn't exist in this survey",
  traitDoesntExist: "Referred field {index} doesn't exist in this survey",
  syntaxError: 'SYNTAX ERROR',
  clusterDoesntExist: "Referred cluster '{index}' doesn't exist for {scope}",
  notValidRule: '{rule} is not a valid aytm Logic rule',
  enableAutocomplete: 'To enable autocomplete press {hotkeys}',
  disableAutocomplete: 'To disable autocomplete press {hotkeys}'
}

const STORAGE_KEY = 'stats-autocomplete'
const EVENT_KEY = 'statsKeydownEvent'
const HOTKEYS = 'CTRL + ['

// Anything but an explicit 'false' reads as enabled, so an unset setting is already on. The toggle
// has to invert that effective answer rather than the stored string, or the first press from a fresh
// profile writes the value it already behaves as and nothing on screen moves.
const autocompleteEnabled = () => 'false' !== window.localStorage.getItem(STORAGE_KEY)

const toggleAutocomplete = () => {
  window.localStorage.setItem(STORAGE_KEY, String(!autocompleteEnabled()))
  const event = document.createEvent('Event')
  event.initEvent(EVENT_KEY, true, true)
  dispatchEvent(event)
}

// The shortcut is an editor affordance, so it acts only while at least one editor is on screen
let mountedEditors = 0

export const registerAutocompleteEditor = () => {
  mountedEditors += 1
  let registered = true
  return () => {
    if (registered) mountedEditors -= 1
    registered = false
  }
}

// Global keydown handler - only add once
const handleGlobalKeydown = event => {
  if (0 < mountedEditors && event.ctrlKey && '[' === event.key) {
    toggleAutocomplete()
  }
}

// Prevent duplicate listeners on HMR
if (!window.__advancedFilterKeydownListenerAdded) {
  window.addEventListener('keydown', handleGlobalKeydown)
  window.__advancedFilterKeydownListenerAdded = true
}

export default class AdvancedFilter extends React.Component {
  static propTypes = {
    dataTestID: PropTypes.string,
    className: PropTypes.string,
    parserResultEof: PropTypes.object.isRequired,
    parserResultPattern: PropTypes.object.isRequired,
    surveySchema: PropTypes.object.isRequired,
    hintPosition: PropTypes.oneOf(['top', 'bottom']),
    pattern: PropTypes.string.isRequired,
    prefix: PropTypes.string,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onActive: PropTypes.func
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { parserResultPattern, pattern } = nextProps
    let newState = null

    if (pattern !== prevState.pattern) {
      newState = { pattern }
    }

    if (parserResultPattern !== prevState.parserResultPattern) {
      const parserState = {
        parserResultPattern,
        errors: fromJS(parserResultPattern.expression.status.errors)
      }
      newState = newState ? Object.assign({}, newState, parserState) : parserState
    }

    return newState
  }

  constructor(props) {
    super(props)
    this.createDecorators = this.createDecorators.bind(this)
    this.createPlugins = this.createPlugins.bind(this)
    this.canAutocompleteBeActive = this.canAutocompleteBeActive.bind(this)
    this.onAutocompleteUpdate = this.onAutocompleteUpdate.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onEditorChange = this.onEditorChange.bind(this)
    this.onEditorUpdate = this.onEditorUpdate.bind(this)
    this.onEditorFocus = this.onEditorFocus.bind(this)
    this.onEditorBlur = this.onEditorBlur.bind(this)
    this.handlePastedText = this.handlePastedText.bind(this)
    this.onSuggestionsBeforeSelect = this.onSuggestionsBeforeSelect.bind(this)
    this.setActiveState = this.setActiveState.bind(this)
    this.updateDecoratorErrors = this.updateDecoratorErrors.bind(this)
    this.updateSuggestionsComponent = this.updateSuggestionsComponent.bind(this)
    this.calculateCursorStyles = this.calculateCursorStyles.bind(this)
    this.getSuggestionPlugin = this.getSuggestionPlugin.bind(this)
    this.renderSuggestions = this.renderSuggestions.bind(this)
    this.renderHint = this.renderHint.bind(this)
    const { parserResultEof, parserResultPattern, surveySchema } = props

    this.state = {
      parserResultEof,
      surveySchema,
      active: false,
      hovered: false,
      cursorTextStyle: null,
      errors: List(),
      showHint: false
    }

    this.parserTimer = null
    this.editorChangeTimer = null
    this.editorUpdateTimer = null

    this.refSuggestionsComponent = null

    this.pluginStore = {
      canAutocompleteBeActive: this.canAutocompleteBeActive,
      getSuggestionsData: (beforeCursor, afterCursor) => {
        return {
          aytmLogic: extractAytmLogic(surveySchema),
          parserResult: extractParserResult(beforeCursor, afterCursor, parserResultPattern, parserResultEof)
        }
      }
    }

    this.decoratorErrorsStore = {
      strategy: ErrorsDecoratorStrategy,
      getLocale: () => Map(AYMT_LOCALE),
      getErrors: () => this.state.errors
    }

    this.plugins = this.createPlugins()
    this.decorators = this.createDecorators()
  }

  createDecorators() {
    return [ErrorsDecoratorFactory(this.decoratorErrorsStore)]
  }

  createPlugins() {
    return [
      createSuggestionsPlugin(this.pluginStore, {
        extractSelection,
        suggestionsBuilders,
        SuggestionsComponent
      })
    ]
  }

  canAutocompleteBeActive() {
    return true !== this.props.disabled && autocompleteEnabled()
  }

  onAutocompleteUpdate() {
    this.forceUpdate(() => {
      this.updateSuggestionsComponent()
    })
  }

  onMouseEnter() {
    const { active, showHint } = this.state
    const { hintPosition } = this.props
    if (!showHint) {
      this.setState({
        showHint: 'top' === hintPosition || !active,
        hovered: true
      })
    } else {
      this.setState({
        hovered: true
      })
    }
  }

  onMouseLeave() {
    const { active, showHint } = this.state
    if (!active && showHint) {
      this.setState({
        showHint: false,
        hovered: false
      })
    } else {
      this.setState({
        hovered: false
      })
    }
  }

  onEditorChange(getContent) {
    const { onChange } = this.props
    const { html } = getContent({ text: false, html: true })

    this.editorChangeTimer && clearTimeout(this.editorChangeTimer)
    this.editorChangeTimer = setTimeout(() => {
      this.pluginStore.canAutocompleteBeActive = this.canAutocompleteBeActive

      this.updateSuggestionsComponent()
    })

    'function' == typeof onChange && onChange(html)
  }

  onEditorUpdate(editorState, prevEditorState) {
    this.pluginStore.canAutocompleteBeActive = this.canAutocompleteBeActive

    const { onUpdate } = this.props
    const nextPattern = editorState.getCurrentContent().getPlainText()

    if (nextPattern != this.state.pattern) {
      this.pluginStore.canAutocompleteBeActive = () => false
    } else {
      this.editorUpdateTimer && clearTimeout(this.editorUpdateTimer)
      this.editorUpdateTimer = setTimeout(() => {
        this.updateDecoratorErrors()
      })
    }

    'function' == typeof onUpdate && onUpdate(editorState, prevEditorState)
  }

  onEditorFocus(event, editorState) {
    const { onFocus } = this.props
    'function' == typeof onFocus && onFocus(event, editorState)

    this.setActiveState(true)
  }

  onEditorBlur(event, editorState) {
    const { onBlur } = this.props
    'function' == typeof onBlur && onBlur(event, editorState)

    this.setActiveState(false)
  }

  handlePastedText(_text, _html, editorState, callback) {
    const parts = _text
      .split(/\s*,\s*/)
      .flatMap(v => v.split(/\s+/))
      .filter(v => 0 < v.trim().length)

    if (parts.every(v => Number.isInteger(Number(v)))) {
      const text = parts.join(', ')

      const content = editorState.getCurrentContent()
      const selection = editorState.getSelection()

      const newContent = Modifier.replaceText(content, selection, text)

      const lastBlock = newContent.getLastBlock()
      const selectionState = new SelectionState({
        anchorKey: lastBlock.getKey(),
        anchorOffset: lastBlock.getLength(),
        focusKey: lastBlock.getKey(),
        focusOffset: lastBlock.getLength()
      })

      const newSelection = new SelectionState({
        anchorKey: content.getLastBlock().getKey(),
        anchorOffset: 0,
        focusKey: content.getFirstBlock().getKey(),
        focusOffset: content.getLastBlock().getLength()
      })
      const newText = Modifier.removeRange(newContent, selectionState, 'backward').getPlainText('\n')
      const newEditorState = EditorState.push(editorState, Modifier.replaceText(content, newSelection, newText))

      const key = newEditorState.getCurrentContent().getLastBlock().getKey()
      const offset = Math.min(
        (selection.getIsBackward() ? selection.getFocusOffset() : selection.getAnchorOffset()) + text.length,
        newText.length
      )

      callback(
        EditorState.forceSelection(
          newEditorState,
          new SelectionState({
            anchorKey: key,
            anchorOffset: offset,
            focusKey: key,
            focusOffset: offset,
            hasFocus: true
          })
        )
      )

      return 'handled'
    }

    return 'non-handled'
  }

  onSuggestionsBeforeSelect() {
    this.pluginStore.canAutocompleteBeActive = this.canAutocompleteBeActive
  }

  setActiveState(active) {
    const { hovered } = this.state
    const { onActive, hintPosition = 'bottom' } = this.props
    const showHint = ((active || hovered) && 'top' === hintPosition) || (!active && 'top' !== hintPosition)

    'function' == typeof onActive && onActive(active)

    this.setState({ active, showHint }, () => {
      this.parserTimer && clearTimeout(this.parserTimer)

      this.updateDecoratorErrors()
      this.parserTimer = setTimeout(() => {
        this.refText.forceUpdateEditorState()
      }, 0)
    })
  }

  updateDecoratorErrors() {
    const { active, errors } = this.state
    const plugin = this.getSuggestionPlugin()

    this.decoratorErrorsStore.getErrors = active
      ? () => errors.filter(error => 'parse' !== error.get('type'))
      : () => errors

    this.decoratorErrorsStore.strategy = active
      ? plugin
        ? WarningsDecoratorStrategy(plugin.getCursor())
        : null
      : ErrorsDecoratorStrategy
  }

  focus() {
    this.refText && this.refText.setFocus()
  }

  updateSuggestionsComponent() {
    if (this.refText && this.refText.refEditor) {
      this.refText.refEditor.onChange(this.refText.state.editorState)
    }
  }

  calculateCursorStyles() {
    if (
      this.refText &&
      this.refText.refEditor &&
      this.refText.refEditor.editor &&
      this.refText.refEditor.editor.editor
    ) {
      const element = this.refText.refEditor.editor.editor
      const style = window.getComputedStyle(element)

      this.setState({
        cursorTextStyle: style ? style.font : null
      })
    }
  }

  componentDidMount() {
    this.unregisterEditor = registerAutocompleteEditor()
    window.addEventListener(EVENT_KEY, this.onAutocompleteUpdate)

    setTimeout(() => {
      this.calculateCursorStyles()
    })
  }

  componentWillUnmount() {
    this.unregisterEditor && this.unregisterEditor()
    this.parserTimer && clearTimeout(this.parserTimer)
    this.editorChangeTimer && clearTimeout(this.editorChangeTimer)
    this.editorUpdateTimer && clearTimeout(this.editorUpdateTimer)

    window.removeEventListener(EVENT_KEY, this.onAutocompleteUpdate)
  }

  componentDidUpdate(prevProps, prevState) {
    const { parserResultEof, parserResultPattern, surveySchema, pattern, active } = this.state

    if (prevProps.surveySchema != surveySchema) {
      this.setState({
        surveySchema: prevProps.surveySchema
      })
      return
    }

    this.pluginStore.getSuggestionsData = (beforeCursor, afterCursor) => {
      return {
        aytmLogic: extractAytmLogic(surveySchema),
        parserResult: extractParserResult(beforeCursor, afterCursor, parserResultPattern, parserResultEof)
      }
    }

    if (prevState.parserResultPattern != parserResultPattern || prevState.pattern != pattern) {
      this.parserTimer && clearTimeout(this.parserTimer)
      this.parserTimer = setTimeout(
        () => {
          this.updateDecoratorErrors()
          this.refText.forceUpdateEditorState()
        },
        active ? 500 : 0
      )
    }
  }

  getSuggestionPlugin() {
    return this.plugins && this.plugins[0] ? this.plugins[0] : null
  }

  renderSuggestions() {
    const plugin = this.getSuggestionPlugin()

    if (!plugin || !plugin.SuggestionsComponent) {
      return null
    }

    const SuggestionsComponent = plugin.SuggestionsComponent

    return (
      <SuggestionsComponent
        ref={ref => (this.refSuggestionsComponent = ref)}
        onBeforeSelect={this.onSuggestionsBeforeSelect}
        cursorTextStyle={this.state.cursorTextStyle}
      />
    )
  }

  renderHint(targetPosition) {
    const { hintPosition = 'bottom' } = this.props
    const { showHint } = this.state

    if (targetPosition !== hintPosition) {
      return null
    }

    return (
      <div
        className={classNames({
          [style['hint']]: true,
          [style['hint--top']]: 'top' === hintPosition,
          [style['hint--bottom']]: 'bottom' === hintPosition
        })}
      >
        <div
          className={classNames({
            [style['hint-text']]: true,
            [style['hint-text--active']]: showHint
          })}
          onClick={toggleAutocomplete}
        >
          {this.canAutocompleteBeActive()
            ? AYMT_LOCALE.disableAutocomplete.replace('{hotkeys}', HOTKEYS)
            : AYMT_LOCALE.enableAutocomplete.replace('{hotkeys}', HOTKEYS)}
        </div>
      </div>
    )
  }

  render() {
    const { dataTestID, className, prefix, disabled } = this.props
    const { pattern, active, errors } = this.state
    const refErrorsCount = errors.filter(v => ['ref', 'cluster'].includes(v.get('type'))).size
    const isWarning = pattern ? 0 < errors.size && refErrorsCount == errors.size : false
    const isError = pattern ? refErrorsCount != errors.size : false
    const readOnly = true === disabled
    const withReadOnly = fn => (readOnly ? undefined : fn)

    return (
      <div
        data-testid={dataTestID || null}
        className={style['widget']}
        onMouseEnter={withReadOnly(this.onMouseEnter)}
        onMouseLeave={withReadOnly(this.onMouseLeave)}
      >
        {this.renderHint('top')}
        <div
          className={classNames(className, style.container, {
            [style['container-active']]: active,
            [style['container-warning']]: !active && isWarning,
            [style['container-error']]: !active && isError,
            [style['container-disabled']]: readOnly,
            'p-disabled': readOnly
          })}
        >
          <div className={style.text}>
            <div className={style['text-input']} key="input">
              <div className={style['text-input-container']}>
                {prefix && <div className={style.prefix} dangerouslySetInnerHTML={{ __html: prefix }} />}
                <RichText
                  ref={el => (this.refText = el)}
                  html={pattern}
                  plugins={this.plugins}
                  decorators={this.decorators}
                  allowUndo
                  multiline={false}
                  spellCheck={false}
                  disableFormatting
                  readOnly={readOnly}
                  onChange={this.onEditorChange}
                  onUpdate={this.onEditorUpdate}
                  onFocus={withReadOnly(this.onEditorFocus)}
                  onBlur={withReadOnly(this.onEditorBlur)}
                  handlePastedText={this.handlePastedText}
                />
                {this.renderSuggestions()}
              </div>
            </div>
          </div>
        </div>
        {this.renderHint('bottom')}
      </div>
    )
  }
}
